import { ChangeEvent, memo, ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import { FormHelperText, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { SelectProps, SelectVariants } from '@mui/material/Select';

import { SelectFieldField, SelectFieldResetIcon, SelectFieldRoot } from './SelectField.style';

interface SelectFieldProps extends Omit<SelectProps, 'onChange' | 'variant' | 'fullWidth' | 'multiple'> {
  onChange?: (e: ChangeEvent<HTMLInputElement>, child: ReactNode) => void;
  isFullWidth?: boolean;
  isClearable?: boolean;
  isMultiple?: boolean;
  variant?: SelectVariants;
  tooltip?: string;
  helperText?: string;
}

function SelectField(props: SelectFieldProps) {
  const {
    children,
    label,
    isFullWidth = true,
    isClearable,
    value,
    name,
    onChange,
    margin,
    variant = 'outlined',
    id = uuid(),
    isMultiple,
    tooltip,
    helperText,
    size,
    ...rest
  } = props;

  function onReset() {
    onChange?.(
      {
        target: {
          name: name ?? '',
          value: '',
        },
      } as unknown as ChangeEvent<HTMLInputElement>,
      null,
    );
  }

  function onChangeHandler({ target }: ChangeEvent<HTMLInputElement>) {
    onChange?.(
      {
        target: {
          name: name ?? '',
          value: target.value,
        },
      } as unknown as ChangeEvent<HTMLInputElement>,
      null,
    );
  }

  const shouldRenderClearIcon =
    isClearable &&
    ((Array.isArray(value) && (value as unknown[]).length > 0) || (typeof value === 'string' && !!value));

  return (
    <Tooltip placement="top" title={tooltip || ''}>
      <SelectFieldRoot fullWidth={isFullWidth} margin={margin} variant={variant} size={size}>
        {label && <InputLabel id={id}>{label}</InputLabel>}

        <SelectFieldField
          inputProps={{ id }}
          value={value}
          label={label}
          onChange={onChangeHandler as SelectProps['onChange']}
          {...rest}
        >
          {children}
        </SelectFieldField>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}

        {shouldRenderClearIcon && (
          <SelectFieldResetIcon>
            <IconButton onClick={onReset} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </SelectFieldResetIcon>
        )}
      </SelectFieldRoot>
    </Tooltip>
  );
}

export default memo(SelectField);
